import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, LogOut as LeaveIcon, Trash2, AlertTriangle, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";

function Section({ title, children, "data-testid": testId }) {
    return (
        <section
            data-testid={testId}
            className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg p-5 sm:p-6"
        >
            <h2 className="font-heading text-xs uppercase tracking-[0.25em] font-bold text-[var(--text-muted)] mb-4">
                {title}
            </h2>
            {children}
        </section>
    );
}

function FieldLabel({ children }) {
    return (
        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1.5">
            {children}
        </label>
    );
}

function TextInput(props) {
    return (
        <input
            {...props}
            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)] disabled:opacity-40"
        />
    );
}

function PrimaryButton({ busy, children, ...rest }) {
    return (
        <button
            {...rest}
            disabled={busy || rest.disabled}
            className="inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-widest text-xs px-4 py-2 rounded hover:bg-[var(--accent-hex-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {children}
        </button>
    );
}

function DangerButton({ busy, children, "data-testid": testId, ...rest }) {
    return (
        <button
            {...rest}
            data-testid={testId}
            disabled={busy || rest.disabled}
            className="inline-flex items-center gap-2 bg-transparent border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white font-extrabold uppercase tracking-widest text-xs px-4 py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {children}
        </button>
    );
}

function ConfirmModal({ open, onClose, onConfirm, title, body, confirmLabel, busy, testIdPrefix }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid={`${testIdPrefix}-modal`}>
            <div className="bg-[var(--bg-card)] border border-[var(--danger)]/50 rounded-lg max-w-md w-full p-6">
                <div className="flex items-center gap-2 text-[var(--danger)] mb-3">
                    <AlertTriangle className="h-5 w-5" />
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">{title}</h3>
                </div>
                <div className="text-sm text-[var(--text-secondary)] mb-4">{body}</div>
                <div className="flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        data-testid={`${testIdPrefix}-cancel`}
                        className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        data-testid={`${testIdPrefix}-confirm`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--danger)] text-white hover:opacity-80 disabled:opacity-40"
                    >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Profile() {
    const { user, refresh, logout } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();

    // ── Identity (name/email) ──────────────────────────────────────────────
    // Draft overlay: null means "use current user value". When the user types,
    // we shift to the draft; after save we clear the draft so the field
    // re-derives from the freshly refreshed `user` object.
    const [nameDraft, setNameDraft] = useState(null);
    const [emailDraft, setEmailDraft] = useState(null);
    const name = nameDraft ?? user?.name ?? "";
    const email = emailDraft ?? user?.email ?? "";
    const [pwForEmail, setPwForEmail] = useState("");
    const [identityBusy, setIdentityBusy] = useState(false);

    // ── Password ──────────────────────────────────────────────────────────
    const [curPw, setCurPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [newPw2, setNewPw2] = useState("");
    const [pwBusy, setPwBusy] = useState(false);

    // ── Pool memberships ──────────────────────────────────────────────────
    const [pools, setPools] = useState([]);
    const [leaveTarget, setLeaveTarget] = useState(null); // { id, name }
    const [leaveBusy, setLeaveBusy] = useState(false);

    // ── Delete account ────────────────────────────────────────────────────
    const [delPw, setDelPw] = useState("");
    const [showDel, setShowDel] = useState(false);
    const [delBusy, setDelBusy] = useState(false);

    // Seed pool memberships (identity uses draft overlay above — no effect needed)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/pools/mine");
                if (!cancelled) setPools(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const saveIdentity = async () => {
        const payload = {};
        if (name.trim() && name.trim() !== user.name) payload.name = name.trim();
        const newEmail = email.trim().toLowerCase();
        if (newEmail && newEmail !== user.email) {
            if (!pwForEmail) {
                toast.error(t("profile.email.need_password"));
                return;
            }
            payload.email = newEmail;
            payload.current_password = pwForEmail;
        }
        if (!Object.keys(payload).length) {
            toast(t("profile.identity.no_change"));
            return;
        }
        setIdentityBusy(true);
        try {
            await api.patch("/auth/me", payload);
            await refresh();
            setNameDraft(null);
            setEmailDraft(null);
            setPwForEmail("");
            toast.success(t("profile.identity.saved"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setIdentityBusy(false); }
    };

    const changePassword = async () => {
        if (newPw !== newPw2) {
            toast.error(t("profile.password.mismatch"));
            return;
        }
        if (newPw.length < 6) {
            toast.error(t("profile.password.too_short"));
            return;
        }
        setPwBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
            setCurPw(""); setNewPw(""); setNewPw2("");
            toast.success(t("profile.password.saved"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setPwBusy(false); }
    };

    const leavePool = async () => {
        if (!leaveTarget) return;
        setLeaveBusy(true);
        try {
            const { data } = await api.delete(`/pools/${leaveTarget.id}/leave`);
            toast.success(t("profile.leave.done", { n: data.deleted_predictions || 0 }));
            setPools((prev) => prev.filter((p) => p.id !== leaveTarget.id));
            setLeaveTarget(null);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setLeaveBusy(false); }
    };

    const deleteAccount = async () => {
        setDelBusy(true);
        try {
            await api.delete("/auth/me", { data: { current_password: delPw } });
            toast.success(t("profile.delete.done"));
            await logout();
            nav("/");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setDelBusy(false); }
    };

    if (!user) return null;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="profile-page">
            <header className="mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]">
                    {t("profile.title")}
                </h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{t("profile.subtitle")}</p>
            </header>

            {/* Identity */}
            <Section title={t("profile.identity.title")} data-testid="profile-identity">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <FieldLabel><User className="inline h-3 w-3 mr-1" />{t("profile.name")}</FieldLabel>
                        <TextInput
                            type="text"
                            value={name}
                            onChange={(e) => setNameDraft(e.target.value)}
                            maxLength={60}
                            data-testid="profile-name-input"
                        />
                    </div>
                    <div>
                        <FieldLabel><Mail className="inline h-3 w-3 mr-1" />{t("profile.email")}</FieldLabel>
                        <TextInput
                            type="email"
                            value={email}
                            onChange={(e) => setEmailDraft(e.target.value)}
                            data-testid="profile-email-input"
                        />
                    </div>
                    {email.trim().toLowerCase() !== user.email && (
                        <div className="sm:col-span-2">
                            <FieldLabel><Lock className="inline h-3 w-3 mr-1" />{t("profile.email.password_label")}</FieldLabel>
                            <TextInput
                                type="password"
                                value={pwForEmail}
                                onChange={(e) => setPwForEmail(e.target.value)}
                                autoComplete="current-password"
                                placeholder={t("profile.email.password_placeholder")}
                                data-testid="profile-email-password"
                            />
                            <p className="text-[11px] text-[var(--text-muted)] mt-1">{t("profile.email.hint")}</p>
                        </div>
                    )}
                </div>
                <div className="mt-4 flex justify-end">
                    <PrimaryButton onClick={saveIdentity} busy={identityBusy} data-testid="profile-identity-save">
                        {t("profile.save")}
                    </PrimaryButton>
                </div>
            </Section>

            {/* Password */}
            <Section title={t("profile.password.title")} data-testid="profile-password">
                <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                        <FieldLabel>{t("profile.password.current")}</FieldLabel>
                        <TextInput type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)}
                            autoComplete="current-password" data-testid="profile-current-password" />
                    </div>
                    <div>
                        <FieldLabel>{t("profile.password.new")}</FieldLabel>
                        <TextInput type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                            autoComplete="new-password" data-testid="profile-new-password" />
                    </div>
                    <div>
                        <FieldLabel>{t("profile.password.confirm")}</FieldLabel>
                        <TextInput type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                            autoComplete="new-password" data-testid="profile-confirm-password" />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <PrimaryButton
                        onClick={changePassword}
                        busy={pwBusy}
                        disabled={!curPw || !newPw || !newPw2}
                        data-testid="profile-password-save"
                    >
                        {t("profile.password.save")}
                    </PrimaryButton>
                </div>
            </Section>

            {/* Pools */}
            <Section title={t("profile.pools.title")} data-testid="profile-pools">
                {pools.length === 0 ? (
                    <p className="text-sm text-[var(--text-muted)]">{t("profile.pools.empty")}</p>
                ) : (
                    <ul className="divide-y divide-[var(--border-base)]">
                        {pools.map((p) => {
                            const isAdmin = p.is_admin || p.admin_id === user.id;
                            return (
                                <li
                                    key={p.id}
                                    className="py-3 flex items-center justify-between gap-3"
                                    data-testid={`profile-pool-${p.id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="font-bold text-sm text-[var(--text-primary)] truncate">{p.name}</p>
                                        <p className="text-[11px] text-[var(--text-muted)] uppercase tracking-widest">
                                            {isAdmin ? t("profile.pools.you_admin") : t("profile.pools.member")}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setLeaveTarget({ id: p.id, name: p.name })}
                                        disabled={isAdmin}
                                        title={isAdmin ? t("profile.pools.admin_cannot_leave") : ""}
                                        data-testid={`profile-leave-${p.id}`}
                                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--danger)] border border-[var(--border-base)] hover:border-[var(--danger)] px-3 py-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <LeaveIcon className="h-3 w-3" /> {t("profile.pools.leave")}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Section>

            {/* Danger zone */}
            <Section title={t("profile.danger.title")} data-testid="profile-danger">
                <p className="text-sm text-[var(--text-secondary)] mb-3">{t("profile.danger.body")}</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="sm:max-w-xs flex-1">
                        <FieldLabel>{t("profile.delete.password_label")}</FieldLabel>
                        <TextInput
                            type="password"
                            value={delPw}
                            onChange={(e) => setDelPw(e.target.value)}
                            autoComplete="current-password"
                            data-testid="profile-delete-password"
                        />
                    </div>
                    <DangerButton
                        onClick={() => setShowDel(true)}
                        busy={delBusy}
                        disabled={!delPw}
                        data-testid="profile-delete-account"
                    >
                        {t("profile.delete.button")}
                    </DangerButton>
                </div>
            </Section>

            {/* Modals */}
            <ConfirmModal
                open={!!leaveTarget}
                onClose={() => setLeaveTarget(null)}
                onConfirm={leavePool}
                title={t("profile.leave.title")}
                body={
                    <>
                        <p className="mb-2">{t("profile.leave.body1", { name: leaveTarget?.name || "" })}</p>
                        <p className="text-[var(--danger)] font-bold">{t("profile.leave.body2")}</p>
                    </>
                }
                confirmLabel={t("profile.leave.confirm")}
                busy={leaveBusy}
                testIdPrefix="profile-leave"
            />

            <ConfirmModal
                open={showDel}
                onClose={() => setShowDel(false)}
                onConfirm={deleteAccount}
                title={t("profile.delete.title")}
                body={
                    <>
                        <p className="mb-2">{t("profile.delete.body1")}</p>
                        <p className="text-[var(--danger)] font-bold">{t("profile.delete.body2")}</p>
                    </>
                }
                confirmLabel={t("profile.delete.confirm")}
                busy={delBusy}
                testIdPrefix="profile-delete"
            />
        </div>
    );
}
